import React, { useEffect, useState } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, CheckCircle2, XCircle, ShieldCheck, Mail, Phone, Hash, MapPin,
  Calendar, FileSignature, User,
} from 'lucide-react';
import { toast } from 'sonner';
import { agentApplicationAPI, agencyAPI } from '@/lib/api';
import { formatPhone } from '@/lib/utils';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const STATUS_META = {
  pending: { label: 'Awaiting review', cls: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-800' },
  rejected: { label: 'Declined', cls: 'bg-rose-100 text-rose-700' },
};

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon size={15} className="text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
        <div className="text-sm font-medium text-slate-800 break-words">{value}</div>
      </div>
    </div>
  );
}

export function ApplicationReviewDrawer({ open, onOpenChange, applicationId, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [app, setApp] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [agencyId, setAgencyId] = useState('');
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (!open || !applicationId) return;
    setLoading(true);
    setApp(null);
    setAgencyId('');
    setRejectReason('');
    agentApplicationAPI.get(applicationId)
      .then((r) => setApp(r.data))
      .catch(() => toast.error('Could not load the application'))
      .finally(() => setLoading(false));
    agencyAPI.list()
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.agencies || r.data?.items || []);
        setAgencies(list);
      })
      .catch(() => {});
  }, [open, applicationId]);

  const approve = async () => {
    if (!agencyId) { toast.error('Please assign an agency first'); return; }
    setBusy(true);
    try {
      const { data } = await agentApplicationAPI.approve(applicationId, agencyId);
      toast.success(`${data.agent_name} approved — portal access sent`);
      onOpenChange(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not approve the application');
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await agentApplicationAPI.reject(applicationId, rejectReason.trim() || null);
      toast.success('Application declined');
      setRejectOpen(false);
      onOpenChange(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not decline the application');
    } finally {
      setBusy(false);
    }
  };

  const name = app ? `${app.first_name || ''} ${app.last_name || ''}`.trim() : '';
  const meta = STATUS_META[app?.status] || STATUS_META.pending;
  const isPending = app?.status === 'pending';

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto overflow-x-hidden px-5 sm:px-6" data-testid="application-review-drawer">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <User size={18} /> {name || 'Application'}
            </SheetTitle>
            <SheetDescription>Public agent onboarding application</SheetDescription>
          </SheetHeader>

          {loading || !app ? (
            <div className="flex h-40 items-center justify-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              <Badge className={`${meta.cls} border-0`} data-testid="application-status-badge">{meta.label}</Badge>

              <div className="rounded-xl border border-slate-200 px-4 py-2 divide-y divide-slate-100">
                <Row icon={Mail} label="Email" value={app.email} />
                <Row icon={Phone} label="Phone" value={formatPhone(app.phone)} />
                <Row icon={Hash} label="NPN" value={app.npn} />
                <Row icon={ShieldCheck} label="SSN" value={app.ssn_last4 ? `•••-••-${app.ssn_last4}` : null} />
                <Row icon={Calendar} label="Date of birth" value={app.date_of_birth} />
                <Row icon={MapPin} label="Address" value={[app.address_line1, app.address_line2, `${app.city || ''}, ${app.state || ''} ${app.zip || ''}`].filter((x) => x && x.trim() && x.trim() !== ',').join(', ')} />
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1.5">
                  <FileSignature size={12} /> Requested contracts
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="application-categories">
                  {(app.category_labels || []).map((c) => (
                    <span key={c} className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200">{c}</span>
                  ))}
                  {(app.category_labels || []).length === 0 && <span className="text-sm text-slate-400">None selected</span>}
                </div>
              </div>

              <div className="text-xs text-slate-400">Submitted {fmtDate(app.submitted_at)}</div>

              {app.status === 'approved' && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800" data-testid="application-approved-note">
                  Approved for <strong>{app.agency_name}</strong> · agent NPN {app.created_agent_npn} created.
                </div>
              )}
              {app.status === 'rejected' && (
                <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-800" data-testid="application-rejected-note">
                  Declined{app.reviewed_by ? ` by ${app.reviewed_by}` : ''}.
                  {app.rejection_reason ? <div className="mt-1 text-rose-700">“{app.rejection_reason}”</div> : null}
                </div>
              )}

              {isPending && (
                <div className="space-y-3 pt-2 border-t border-slate-100">
                  <div>
                    <div className="text-sm font-medium text-slate-700 mb-1.5">Assign agency *</div>
                    <Select value={agencyId} onValueChange={setAgencyId}>
                      <SelectTrigger data-testid="application-agency-select"><SelectValue placeholder="Select an agency…" /></SelectTrigger>
                      <SelectContent>
                        {agencies.map((a) => (
                          <SelectItem key={a.agency_id} value={a.agency_id}>{a.agency_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-slate-400 mt-1">Approving creates the agent and emails their secure portal access.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={approve} disabled={busy || !agencyId} className="flex-1 text-white" style={{ background: '#15794a' }} data-testid="application-approve-btn">
                      {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={15} className="mr-1.5" />}
                      Approve
                    </Button>
                    <Button variant="outline" onClick={() => setRejectOpen(true)} disabled={busy} className="flex-1 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="application-reject-btn">
                      <XCircle size={15} className="mr-1.5" /> Decline
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <AlertDialogContent data-testid="application-reject-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Decline this application?</AlertDialogTitle>
            <AlertDialogDescription>
              The applicant will be emailed that they were not approved. You can add an optional note they'll see.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            placeholder="Optional note to the applicant…"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            data-testid="application-reject-reason"
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={reject} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="application-reject-confirm">
              {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null} Decline application
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default ApplicationReviewDrawer;
